/**
 * CartItemList — renders a list of CartItem rows separated by hairline dividers.
 * Wraps each item in SwipeableItem on mobile for swipe-to-remove.
 */
import PropTypes from 'prop-types';

import {
  Box,
  Stack,
  useBreakpointValue,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import CartItem from '@amblique/king-living/app/components/cart-flyout/cart-item';
import SwipeableItem from '@amblique/king-living/app/components/cart-flyout/swipeable-item';

const CartItemList = ({
  currency,
  items,
  onChangeItemQuantity,
  onEditItem,
  onRemoveItem,
  outOfStockItemIds = new Set(),
  productsByItemId,
  productsByProductId,
  promotionsById,
  removingItemIds,
  updatingItemIds,
}) => {
  const isMobile = useBreakpointValue({base: true, lg: false});
  const styles = useMultiStyleConfig('CartFlyout', {});

  return (
    // role="list" restores list semantics lost when CSS `list-style: none` is applied
    <Stack
      className="cart-item-list__root"
      paddingBottom="8px"
      role="list"
      spacing={4}
    >
      {items.map((lineItem, index) => {
        const isLast = index === items.length - 1;
        // An item is in-flight if any of its IDs (parent or children) are being mutated.
        const relatedIds = [lineItem.itemId, ...(lineItem._childItems ?? []).map((c) => c.itemId)];
        const isRemoving = relatedIds.some((id) => removingItemIds.has(id));
        const isUpdating = relatedIds.some((id) => updatingItemIds.has(id));

        const cartItem = (
          <CartItem
            currency={currency}
            isEditDisabled
            isOutOfStock={outOfStockItemIds.has(lineItem.itemId)}
            isRemoving={isRemoving}
            isUpdating={isUpdating}
            lineItem={lineItem}
            productsByItemId={productsByItemId}
            productsByProductId={productsByProductId}
            promotionsById={promotionsById}
            onChangeQuantity={onChangeItemQuantity}
            onEdit={onEditItem}
            onRemove={onRemoveItem}
          />
        );

        const divider = !isLast ? (
          <Box className="cart-item-list__divider" {...styles.itemDivider} />
        ) : null;

        if (isMobile) {
          return (
            <Box
              key={lineItem.itemId}
              className="cart-item-list__item-wrapper"
              role="listitem"
              width="100%"
            >
              <SwipeableItem
                showDivider={!isLast}
                onSwipeDelete={() => onRemoveItem(lineItem)}
              >
                <Box className="cart-item-list__item" {...styles.lineItem}>
                  {cartItem}
                </Box>
              </SwipeableItem>
            </Box>
          );
        }

        return (
          <Box
            key={lineItem.itemId}
            className="cart-item-list__item"
            role="listitem"
            {...styles.lineItem}
          >
            {cartItem}
            {divider}
          </Box>
        );
      })}
    </Stack>
  );
};

CartItemList.displayName = 'CartItemList';
CartItemList.propTypes = {
  currency: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  onChangeItemQuantity: PropTypes.func.isRequired,
  onEditItem: PropTypes.func.isRequired,
  onRemoveItem: PropTypes.func.isRequired,
  outOfStockItemIds: PropTypes.instanceOf(Set),
  productsByItemId: PropTypes.object.isRequired,
  productsByProductId: PropTypes.object,
  promotionsById: PropTypes.object,
  removingItemIds: PropTypes.instanceOf(Set).isRequired,
  updatingItemIds: PropTypes.instanceOf(Set).isRequired,
};

export default CartItemList;
